/**
 * PATH: apps/quote/src/components/builder/panels/inspector/OverridesTab.tsx
 * FILE_ID: QB-INSPECTOR-OVERRIDES-003
 * PURPOSE: Manual overrides tab for the row inspector — adjusted rate +
 *          5 per-line discipline overrides (markup/wastage/downtime) + clears.
 * WHY: Estimators need to override the project-wide markup/wastage/downtime
 *      values on a per-row basis when one line behaves differently from
 *      the rest of the scope (e.g. a tight access area gets 30% downtime
 *      instead of the project default 10%). Backend support shipped in
 *      MR 0–3 of this work (migration 0025 + CalculationEngine 3-tier
 *      resolution + serializer exposure). This file is the user-facing
 *      surface for those backend fields.
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────┐   ┌──────────────────────────┐
 *   │ user edits one   │ → │ controlled state per│ → │ onUpdate({patch})        │
 *   │ of 6 controls    │   │ row + commit-on-blur│   │  → RowInspectorPanel     │
 *   └──────────────────┘   └─────────────────────┘   │  → onUpdateLineItem(...) │
 *                                                    └──────────────────────────┘
 * DEPENDENCIES:
 *   - QuoteLineItem / QuoteDetail: row + quote shapes (config holds defaults).
 *   - AdjustedRateRow + MarkupOverrideRow: split rows (300-line rule).
 *   - overrides-helpers: parse/format + config resolution (pure I→O).
 *   - formatCurrency: currency display in totals box.
 * RELATED:
 *   - panels/RowInspectorPanel.tsx: mounts this tab + supplies onUpdate.
 *   - panels/inspector/AdvancedTab.tsx: same onUpdate(patch) contract.
 *   - backend services/calculation_engine.py: consuming side of the patches.
 */

import { AlertCircle } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { QuoteDetail, QuoteLineItem } from '@/lib/quoteApi'
import AdjustedRateRow from './AdjustedRateRow'
import MarkupOverrideRow from './MarkupOverrideRow'
import { resolveConfigDefault, type DisciplineConfigKey } from './overrides-helpers'

interface OverridesTabProps {
  lineItem: QuoteLineItem
  /**
   * Quote detail — used only for ``quote.config`` so the markup rows can
   * show the right "default: NN%" hint. Optional so the tab still
   * renders in Storybook / isolated contexts (falls back to engine
   * defaults baked into ``overrides-helpers``).
   */
  quote?: QuoteDetail | null
  /**
   * Send a partial patch to be persisted on this line item. Same
   * contract as AdvancedTab — RowInspectorPanel converts to
   * onUpdateLineItem(sectionId, itemId, patch). Optional so this tab
   * remains rendarable in isolated/Storybook contexts.
   */
  onUpdate?: (patch: Partial<QuoteLineItem>) => void
}

interface MarkupRowSpec {
  id: string
  label: string
  value: string | number | null | undefined
  configKey: DisciplineConfigKey
  patchField: keyof QuoteLineItem
}

function buildMarkupRows(lineItem: QuoteLineItem): MarkupRowSpec[] {
  return [
    {
      id: 'material-markup',
      label: 'Material Markup',
      value: lineItem.override_material_markup,
      configKey: 'materialMarkup',
      patchField: 'override_material_markup',
    },
    {
      id: 'material-wastage',
      label: 'Material Wastage',
      value: lineItem.override_material_wastage,
      configKey: 'materialWastage',
      patchField: 'override_material_wastage',
    },
    {
      id: 'plant-markup',
      label: 'Plant Markup',
      value: lineItem.override_plant_markup,
      configKey: 'plantMarkup',
      patchField: 'override_plant_markup',
    },
    {
      id: 'subcon-markup',
      label: 'Subcontractor Markup',
      value: lineItem.override_subcon_markup,
      configKey: 'subcontractorMarkup',
      patchField: 'override_subcon_markup',
    },
    {
      id: 'downtime',
      label: 'Downtime Factor',
      value: lineItem.override_downtime,
      configKey: 'downtimeFactor',
      patchField: 'override_downtime',
    },
  ]
}

export default function OverridesTab({ lineItem, quote, onUpdate }: OverridesTabProps) {
  const pricingMode: 'manual' | 'calculated' =
    lineItem.pricing_mode === 'manual' || lineItem.pricing_mode === 'calculated'
      ? lineItem.pricing_mode
      : lineItem.override_chargeout != null
        ? 'manual'
        : 'calculated'

  const hasAdjustedRate =
    lineItem.adjusted_rate != null &&
    Number.parseFloat(String(lineItem.adjusted_rate)) > 0
  const hasOverrideChargeout = lineItem.override_chargeout != null
  const hasAnyMarkupOverride =
    lineItem.override_material_markup != null ||
    lineItem.override_material_wastage != null ||
    lineItem.override_plant_markup != null ||
    lineItem.override_subcon_markup != null ||
    lineItem.override_downtime != null
  const hasAnyOverride =
    hasAdjustedRate || hasOverrideChargeout || hasAnyMarkupOverride

  // Read config via cast — `config` isn't on the QuoteDetail type but the
  // backend ships it on every quote (see backend models/quote.py).
  const config = (quote?.config ?? null) as Record<string, unknown> | null
  const markupRows = buildMarkupRows(lineItem)

  return (
    <div className="p-3 space-y-3" data-testid="overrides-tab">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Manual Overrides
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Override calculated values when needed
        </p>
      </div>

      <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-2.5 py-1.5">
        <span className="text-xs text-slate-600 dark:text-slate-400">Pricing mode</span>
        <span
          className={
            pricingMode === 'manual'
              ? 'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white'
              : 'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white'
          }
          data-testid="overrides-pricing-mode"
        >
          {pricingMode}
        </span>
      </div>

      {hasAnyOverride && (
        <div
          className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
          data-testid="overrides-banner"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 dark:text-amber-300">
              Manual overrides applied
            </span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <AdjustedRateRow
          value={lineItem.adjusted_rate}
          unit={lineItem.unit ?? 'm²'}
          calculatedRate={Number(lineItem.rate_per_unit ?? 0)}
          onCommit={onUpdate ? (rate) => onUpdate({ adjusted_rate: rate }) : undefined}
          onClear={onUpdate ? () => onUpdate({ adjusted_rate: null }) : undefined}
        />

        <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
          <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Per-line markup &amp; wastage
          </h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            Override the project defaults for this row only. Blank inputs
            fall back to the quote setup config.
          </p>
          <div className="space-y-3" data-testid="overrides-markup-section">
            {markupRows.map((row) => (
              <MarkupOverrideRow
                key={row.id}
                id={row.id}
                label={row.label}
                value={row.value}
                configDefault={resolveConfigDefault(config, row.configKey)}
                onCommit={
                  onUpdate
                    ? (decimalOrNull) =>
                        onUpdate({ [row.patchField]: decimalOrNull } as Partial<QuoteLineItem>)
                    : undefined
                }
              />
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            Override Chargeout Total
          </label>
          <div
            className="w-full px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-900 text-sm tabular-nums text-slate-700 dark:text-slate-200"
            data-testid="overrides-chargeout-display"
          >
            {hasOverrideChargeout
              ? formatCurrency(Number(lineItem.override_chargeout ?? 0))
              : <span className="text-slate-400">— (calculated)</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Set automatically when component chargeouts (Labour / Materials /
            Plant / Subcon) are entered manually in the grid. Edit those
            cells to change this value.
          </p>
        </div>
      </div>

      <div className="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-lg space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Total Cost:</span>
          <span className="font-medium text-slate-900 dark:text-white">
            {formatCurrency(Number(lineItem.total_cost ?? 0))}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Chargeout:</span>
          <span className="font-medium text-emerald-600">
            {formatCurrency(Number(lineItem.chargeout_total ?? 0))}
          </span>
        </div>
        <div className="flex justify-between text-sm pt-1 border-t border-slate-200 dark:border-slate-700">
          <span className="text-slate-600 dark:text-slate-400">Margin:</span>
          <span className="font-medium text-indigo-600">
            {formatCurrency(Number(lineItem.margin ?? 0))} (
            {Number(lineItem.margin_percent ?? 0).toFixed(1)}%)
          </span>
        </div>
      </div>
    </div>
  )
}
